import React, { useState, useMemo } from 'react';
import { Search, ChevronRight, ArrowLeft, Package, ShoppingCart, Users, Cpu, TestTube } from 'lucide-react';
import { getOrderUrgency } from '../kpiUtils';

// ─── Category / view definitions ─────────────────────────────────────────────
const CATEGORIES = [
  {
    id: 'reagents',
    label: 'Reagents',
    icon: TestTube,
    color: 'emerald',
    views: [
      { id: 'all',      label: 'All Reagents' },
      { id: 'low',      label: 'Low Stock' },
      { id: 'critical', label: 'Critical' },
      { id: 'warning',  label: 'Warning' },
      { id: 'healthy',  label: 'Healthy' },
    ],
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: ShoppingCart,
    color: 'blue',
    views: [
      { id: 'all',       label: 'All Orders' },
      { id: 'pending',   label: 'Pending' },
      { id: 'at_risk',   label: 'At Risk' },
      { id: 'overdue',   label: 'Overdue' },
      { id: 'fulfilled', label: 'Fulfilled' },
    ],
  },
  {
    id: 'crew',
    label: 'Crew',
    icon: Users,
    color: 'violet',
    views: [
      { id: 'performance',  label: 'Performance' },
      { id: 'work_logs',    label: 'Work Logs' },
      { id: 'top',          label: 'Top Achievers' },
      { id: 'behind',       label: 'Behind Target' },
    ],
  },
  {
    id: 'hardware',
    label: 'Hardware',
    icon: Cpu,
    color: 'amber',
    views: [
      { id: 'stock',     label: 'Current Stock' },
      { id: 'capacity',  label: 'Build Capacity' },
      { id: 'full_set',  label: 'Full Set' },
      { id: 'device',    label: 'Device' },
      { id: 'shaker',    label: 'Shaker' },
      { id: 'pump',      label: 'Pump' },
      { id: 'low',       label: 'Low Stock' },
    ],
  },
];

const CAT_COLORS = {
  emerald: {
    card: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100',
    icon: 'text-emerald-600',
    label: 'text-emerald-800',
    pill: 'bg-emerald-600 text-white',
    pillOff: 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100',
  },
  blue: {
    card: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
    icon: 'text-blue-600',
    label: 'text-blue-800',
    pill: 'bg-blue-600 text-white',
    pillOff: 'bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100',
  },
  violet: {
    card: 'bg-violet-50 border-violet-200 hover:bg-violet-100',
    icon: 'text-violet-600',
    label: 'text-violet-800',
    pill: 'bg-violet-600 text-white',
    pillOff: 'bg-violet-50 border border-violet-200 text-violet-700 hover:bg-violet-100',
  },
  amber: {
    card: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
    icon: 'text-amber-600',
    label: 'text-amber-800',
    pill: 'bg-amber-600 text-white',
    pillOff: 'bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100',
  },
};

// ─── Data resolvers ───────────────────────────────────────────────────────────
const resolveData = (catId, viewId, data) => {
  if (!data) return [];

  if (catId === 'reagents') {
    const items = (data.inventoryItems || []).filter((i) => i.category === 'reagent');
    if (!items.length && data.stats) {
      return [{ _empty: true, message: `${data.stats.totalReagents} reagents tracked · ${data.stats.lowStockItems} below threshold.` }];
    }
    if (viewId === 'all')      return items;
    if (viewId === 'low')      return items.filter(i => i.current_stock < i.minimum_threshold);
    if (viewId === 'critical') return items.filter(i => i.status === 'critical' || i.status === 'stockout');
    if (viewId === 'warning')  return items.filter(i => i.status === 'warning');
    if (viewId === 'healthy')  return items.filter(i => i.status === 'healthy');
  }

  if (catId === 'orders') {
    const orders = data.upcomingOrders || [];
    if (viewId === 'all') return orders;
    if (viewId === 'pending')   return orders.filter(o => String(o.status || '').toLowerCase() === 'pending');
    if (viewId === 'fulfilled') return orders.filter(o => String(o.status || '').toLowerCase() === 'fulfilled');
    if (viewId === 'at_risk')   return orders.filter(o => {
      const b = getOrderUrgency(o.due_date).bucket;
      return (b === 'at_risk' || b === 'critical') && String(o.status || '').toLowerCase() !== 'fulfilled';
    });
    if (viewId === 'overdue')   return orders.filter(o => getOrderUrgency(o.due_date).bucket === 'overdue');
  }

  if (catId === 'crew') {
    const crew = data.crewPerformance || [];
    const logs = data.workLogs || [];
    if (viewId === 'performance')  return [...crew].sort((a, b) => Number(b.percentage || 0) - Number(a.percentage || 0));
    if (viewId === 'work_logs')    return logs;
    if (viewId === 'top')          return crew.filter(c => Number(c.percentage || 0) >= 100);
    if (viewId === 'behind')       return crew.filter(c => Number(c.percentage || 0) < 60);
  }

  if (catId === 'hardware') {
    const stockList = data.hardwareStockList || [];
    const capList = data.hwIndividuals || [];
    if (viewId === 'stock')    return stockList;
    if (viewId === 'capacity') return capList;
    if (viewId === 'full_set') return data.hwFullSet ? [{ _fullset: true, ...data.hwFullSet }] : [];
    if (viewId === 'device') {
      if (data.deviceCap) return [data.deviceCap];
      if (data.deviceStockVersions?.length) return data.deviceStockVersions;
      return data.deviceStock ? [data.deviceStock] : [];
    }
    if (viewId === 'shaker')   return data.shakerCap ? [data.shakerCap] : (data.shakerStock ? [data.shakerStock] : []);
    if (viewId === 'pump')     return data.pumpCap   ? [data.pumpCap]   : (data.pumpStock   ? [data.pumpStock]   : []);
    if (viewId === 'low')      return stockList.filter((hw) => hw.isLowStock);
    return stockList;
  }

  return [];
};

// ─── Row renderers ────────────────────────────────────────────────────────────
const STATUS_DOT = { critical: 'bg-red-500', warning: 'bg-amber-500', healthy: 'bg-emerald-500', stockout: 'bg-red-700' };

const ReagentRow = ({ item }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-xs">
    <div className="flex items-center gap-2 min-w-0">
      <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[item.status] || 'bg-slate-300'}`} />
      <span className="font-medium text-slate-800 truncate">{item.name}</span>
      <span className="text-slate-400 capitalize shrink-0">{item.unit}</span>
    </div>
    <div className="flex items-center gap-3 shrink-0">
      <span className="tabular-nums text-slate-700 font-medium">{item.current_stock}</span>
      <span className="text-slate-400">/</span>
      <span className="tabular-nums text-slate-400">{item.minimum_threshold} min</span>
      {item.days_remaining < 999 && (
        <span className={`text-[10px] px-1 py-0.5 rounded font-medium ${item.days_remaining <= 2 ? 'bg-red-100 text-red-700' : item.days_remaining <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
          {item.days_remaining}d
        </span>
      )}
    </div>
  </div>
);

const OrderRow = ({ order }) => {
  const { bucket, daysUntilDue } = getOrderUrgency(order.due_date);
  const dueStr = order.due_date ? new Date(order.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—';
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-xs">
      <div className="min-w-0">
        <p className="font-medium text-slate-800 truncate">{order.order_number}</p>
        <p className="text-slate-400 truncate">{order.customer_name}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-slate-400">{dueStr}</span>
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${bucket === 'overdue' ? 'bg-red-100 text-red-700' : bucket === 'critical' ? 'bg-red-50 text-red-600' : bucket === 'at_risk' ? 'bg-amber-100 text-amber-700' : bucket === 'fulfilled' ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-700'}`}>
          {bucket === 'on_track' ? 'ok' : bucket.replace('_', ' ')}
        </span>
      </div>
    </div>
  );
};

const CrewRow = ({ member, isLog }) => {
  if (isLog) {
    const qty = Number(member.quantity_completed || member.quantity_produced || 0);
    const item = member.item_name || member.reagent_name || member.hardware_name || '—';
    return (
      <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-xs">
        <div className="min-w-0">
          <span className="font-medium text-slate-800">{member.worker_name}</span>
          <span className="text-slate-400 ml-2 truncate">{item}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-slate-400">{member.start_time || '—'}–{member.end_time || 'now'}</span>
          <span className="font-bold text-emerald-600">+{qty}</span>
        </div>
      </div>
    );
  }
  const pct = Number(member.percentage || 0);
  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 text-xs">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${pct >= 100 ? 'bg-emerald-100 text-emerald-700' : pct > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
        {(member.worker_name || '?').slice(0, 1).toUpperCase()}
      </div>
      <span className="font-medium text-slate-800 flex-1 truncate">{member.worker_name}</span>
      <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${pct >= 90 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className={`tabular-nums font-semibold w-9 text-right ${pct >= 90 ? 'text-emerald-700' : pct >= 60 ? 'text-amber-700' : 'text-red-700'}`}>{pct}%</span>
    </div>
  );
};

const HardwareRow = ({ hw }) => {
  if (hw._fullset) {
    return (
      <div className="py-3 text-xs">
        <p className="font-semibold text-slate-900 text-sm">Full Set Build Capacity</p>
        <p className="text-3xl font-bold text-slate-900 tabular-nums mt-1">
          {hw.max_capacity} <span className="text-base font-normal text-slate-500">sets</span>
        </p>
        {hw.limiting_component && !hw.limiting_component.includes('No') && (
          <p className="text-amber-700 mt-1">Bottleneck: {hw.limiting_component}</p>
        )}
      </div>
    );
  }

  // BOM build capacity row
  if (hw.max_capacity != null && hw.limiting_component != null) {
    const hasLimiter = hw.limiting_component && !hw.limiting_component.includes('No components') && !hw.limiting_component.includes('No BOM');
    return (
      <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-xs">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900 truncate">{hw.name}</p>
          <p className="text-slate-500">build capacity</p>
          {hasLimiter && <p className="text-amber-700 truncate">Bottleneck: {hw.limiting_component}</p>}
        </div>
        <span className="text-xl font-bold text-slate-900 tabular-nums shrink-0">{hw.max_capacity}</span>
      </div>
    );
  }

  // Current stock row
  const low = hw.isLowStock;
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-xs">
      <div className="min-w-0">
        <p className="font-semibold text-slate-900 truncate">{hw.label || hw.name}</p>
        <p className="text-slate-500">in stock · min {hw.min_quantity}</p>
      </div>
      <span className={`text-xl font-bold tabular-nums shrink-0 ${low ? 'text-red-700' : 'text-slate-900'}`}>
        {hw.current_stock}
      </span>
    </div>
  );
};

const DataRows = ({ catId, viewId, items }) => {
  if (!items.length) {
    return <p className="text-xs text-slate-400 italic py-3 text-center">No items found.</p>;
  }
  if (items[0]?._empty) {
    return <p className="text-xs text-slate-500 py-3 leading-relaxed">{items[0].message}</p>;
  }

  return (
    <div className="max-h-[220px] overflow-y-auto pr-0.5">
      {items.map((item, i) => {
        if (catId === 'reagents') return <ReagentRow key={item.id || i} item={item} />;
        if (catId === 'orders')   return <OrderRow   key={item.id || i} order={item} />;
        if (catId === 'crew')     return <CrewRow    key={item.worker_id || item.id || i} member={item} isLog={viewId === 'work_logs'} />;
        if (catId === 'hardware') return <HardwareRow key={item.hardware_id || item.id || i} hw={item} />;
        return null;
      })}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function DashboardExplorer({ data }) {
  const [search, setSearch]     = useState('');
  const [catId, setCatId]       = useState(null);
  const [viewId, setViewId]     = useState(null);

  const category = CATEGORIES.find(c => c.id === catId);
  const colors   = category ? CAT_COLORS[category.color] : null;

  const filteredCats = useMemo(() =>
    CATEGORIES.filter(c => !search || c.label.toLowerCase().includes(search.toLowerCase()) ||
      c.views.some(v => v.label.toLowerCase().includes(search.toLowerCase()))),
    [search]
  );

  const items = useMemo(
    () => catId && viewId ? resolveData(catId, viewId, data) : [],
    [catId, viewId, data]
  );

  const selectCat = (id) => { setCatId(id); setViewId(null); setSearch(''); };
  const reset     = ()  => { setCatId(null); setViewId(null); };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-full min-h-[340px]">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
        <span className="text-base">🔍</span>
        <h3 className="text-sm font-semibold text-slate-800 flex-1">Data Explorer</h3>
        {catId && (
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
        )}
      </div>

      <div className="flex-1 overflow-hidden flex flex-col p-4 gap-3">
        {/* Search */}
        {!catId && (
          <div className="flex items-center gap-2 rounded-lg px-3 py-2 bg-slate-50 ring-1 ring-slate-200">
            <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <input
              type="text"
              placeholder="Search data…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="text-xs flex-1 bg-transparent outline-none ring-0 focus:ring-0 focus:outline-none !border-0 !rounded-none shadow-none text-slate-700 placeholder-slate-400"
            />
          </div>
        )}

        {/* Breadcrumb */}
        {catId && (
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <button onClick={reset} className="hover:text-slate-700 transition-colors font-medium">{category?.label}</button>
            {viewId && (
              <>
                <ChevronRight className="w-3 h-3" />
                <span className="text-slate-700 font-medium">{category?.views.find(v => v.id === viewId)?.label}</span>
                {items.length > 0 && !items[0]?._empty && (
                  <span className="ml-1 bg-slate-100 text-slate-500 rounded-full px-1.5 py-0.5">{items.length}</span>
                )}
              </>
            )}
          </div>
        )}

        {/* Category grid */}
        {!catId && (
          <div className="grid grid-cols-2 gap-2">
            {filteredCats.map(cat => {
              const c = CAT_COLORS[cat.color];
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => selectCat(cat.id)}
                  className={`flex items-center gap-2.5 p-3 rounded-xl border text-left transition-all ${c.card}`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${c.icon}`} />
                  <div>
                    <p className={`text-xs font-semibold ${c.label}`}>{cat.label}</p>
                    <p className="text-[10px] text-slate-400">{cat.views.length} views</p>
                  </div>
                </button>
              );
            })}
            {filteredCats.length === 0 && (
              <p className="col-span-2 text-xs text-slate-400 italic text-center py-4">No matching category</p>
            )}
          </div>
        )}

        {/* Sub-view pills */}
        {catId && category && (
          <div className="flex flex-wrap gap-1.5">
            {category.views.map(v => (
              <button
                key={v.id}
                onClick={() => setViewId(v.id)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all ${viewId === v.id ? colors.pill : colors.pillOff}`}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}

        {/* Data output */}
        {catId && viewId && (
          <div className="flex-1 overflow-hidden">
            <DataRows catId={catId} viewId={viewId} items={items} />
          </div>
        )}

        {/* Prompt when category selected but no view */}
        {catId && !viewId && (
          <p className="text-xs text-slate-400 italic text-center pt-2">Select a view above to see data</p>
        )}
      </div>
    </div>
  );
}
